import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || window.location.origin;
const NAVY = "#1e3a5f";
const LIGHT_BLUE = "#a8c4e0";
const REDIRECT_SECS = 5;

export default function VerifyEmail() {
  const [state, setState] = useState("loading"); // loading | success | error | network
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(REDIRECT_SECS);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");

    if (!token) {
      setState("error");
      setMessage("No verification token was found in this link.");
      return;
    }

    fetch(`${API}/api/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json();
        if (data.success) {
          setState("success");
          setMessage(data.message);
        } else {
          setState("error");
          setMessage(data.message || "This link is invalid or has expired.");
        }
      })
      .catch(() => {
        setState("network");
        setMessage("Could not reach the server. Please check your connection and try again.");
      });
  }, []);

  // Auto-redirect countdown on success
  useEffect(() => {
    if (state !== "success") return;
    if (countdown <= 0) { window.location.href = "/"; return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [state, countdown]);

  return (
    <div style={{
      minHeight: "100vh", background: "#f0f2f5",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "Arial, sans-serif", padding: 20,
    }}>
      <div style={{
        width: "100%", maxWidth: 460,
        background: "#ffffff", borderRadius: 10, overflow: "hidden",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      }}>
        {/* Header — matches welcome email */}
        <div style={{
          background: NAVY, padding: "28px 32px", textAlign: "center",
        }}>
          <div style={{ color: "#ffffff", fontSize: 22, fontWeight: "bold", letterSpacing: 2 }}>
            ALPHA MEDIA
          </div>
          <div style={{ color: LIGHT_BLUE, fontSize: 11, letterSpacing: 3, marginTop: 4 }}>
            ANALYTICS
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "40px 32px 36px", textAlign: "center" }}>
          {state === "loading" && <LoadingState />}
          {state === "success" && <SuccessState message={message} countdown={countdown} />}
          {state === "error"   && <ErrorState   message={message} />}
          {state === "network" && <NetworkState message={message} />}
        </div>

        {/* Footer */}
        <div style={{
          background: "#f8f9fa", borderTop: "1px solid #e8e8e8",
          padding: "16px 32px", textAlign: "center",
        }}>
          <p style={{ fontSize: 11, color: "#999", margin: 0, lineHeight: 1.7 }}>
            Alpha Media Holdings &middot; Harare, Zimbabwe
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
      <h2 style={{ color: NAVY, fontSize: 20, fontWeight: 800, margin: "0 0 10px", fontFamily: "Georgia, serif" }}>
        Verifying your email…
      </h2>
      <p style={{ color: "#666", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
        Please wait a moment.
      </p>
    </>
  );
}

function SuccessState({ message, countdown }) {
  return (
    <>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: "#dcfce7", display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 20px",
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="12" fill="#22c55e" />
          <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h2 style={{ color: NAVY, fontSize: 22, fontWeight: 800, margin: "0 0 10px", fontFamily: "Georgia, serif" }}>
        Email verified!
      </h2>
      <p style={{ color: "#444", fontSize: 14, margin: "0 0 28px", lineHeight: 1.7 }}>
        {message} You can now sign in to the Alpha Media Analytics dashboard.
      </p>
      <a href="/"
        style={{
          display: "inline-block", background: NAVY, color: "#fff",
          padding: "12px 32px", borderRadius: 6, textDecoration: "none",
          fontSize: 14, fontWeight: "bold", fontFamily: "Georgia, serif",
        }}>
        Go to login
      </a>
      <p style={{ color: "#999", fontSize: 12, margin: "18px 0 0" }}>
        Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}…
      </p>
    </>
  );
}

function ErrorState({ message }) {
  return (
    <>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: "#fef3c7", display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 20px",
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="12" fill="#f59e0b" />
          <path d="M12 7v5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1.2" fill="#fff" />
        </svg>
      </div>
      <h2 style={{ color: NAVY, fontSize: 22, fontWeight: 800, margin: "0 0 10px", fontFamily: "Georgia, serif" }}>
        Verification link expired
      </h2>
      <p style={{ color: "#444", fontSize: 14, margin: "0 0 28px", lineHeight: 1.7 }}>
        {message} Verification links expire after 24 hours or after they have already been used.
      </p>
      <a href="mailto:tech@alphamedia.co.zw"
        style={{
          display: "inline-block", background: NAVY, color: "#fff",
          padding: "12px 32px", borderRadius: 6, textDecoration: "none",
          fontSize: 14, fontWeight: "bold", fontFamily: "Georgia, serif",
        }}>
        Contact tech@alphamedia.co.zw
      </a>
    </>
  );
}

function NetworkState({ message }) {
  return (
    <>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: "#fee2e2", display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 20px",
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="12" fill="#DC2626" />
          <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </div>
      <h2 style={{ color: NAVY, fontSize: 22, fontWeight: 800, margin: "0 0 10px", fontFamily: "Georgia, serif" }}>
        Something went wrong
      </h2>
      <p style={{ color: "#444", fontSize: 14, margin: "0 0 28px", lineHeight: 1.7 }}>
        {message}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: NAVY, color: "#fff", border: "none",
          padding: "12px 32px", borderRadius: 6, cursor: "pointer",
          fontSize: 14, fontWeight: "bold", fontFamily: "Georgia, serif",
        }}>
        Try again
      </button>
    </>
  );
}
